import _ from 'lodash';
import { useEffect, useRef } from 'react';

/**
 * Runs a callback during render once per dependency set and honours the cleanup function it returns. Compares the dependency array against the previous one to run the stored cleanup and re-arm the callback, and also runs that cleanup when the component unmounts.
 *
 * @param callBack - Function run during render, optionally returning a cleanup.
 * @param dependency - Values whose change re-arms the callback.
 * @returns Nothing.
 */
const usePrevRender = (callBack: (...args: any[]) => any = () => {}, dependency: any[] = []) => {
  const hasBeenCalled = useRef(false);
  const prevDependency = useRef<any[]>([]);
  /**
   * Holds a mutable ref to the cleanup function returned by the previous callback invocation. Initialized to a no-op and later reassigned to the callback's return value so it can be run on dependency change or unmount.
   *
   * @returns A mutable ref containing the current cleanup function.
   */
  const cleanupRef = useRef<any>(() => {});

  useEffect(
    () => () => {
      if (_.isFunction(cleanupRef.current)) {
        cleanupRef.current();
      }
    },
    [],
  );

  // Handle clean up
  if (!_.isEqual(dependency, prevDependency.current) && hasBeenCalled.current) {
    hasBeenCalled.current = false;
    if (_.isFunction(cleanupRef.current)) {
      cleanupRef.current();
    }
  }

  prevDependency.current = [...dependency];
  if (hasBeenCalled.current) return;
  cleanupRef.current = callBack();
  hasBeenCalled.current = true;
};

export default usePrevRender;
